'use client';

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, fetchBlogs } from '../api';
import calendarPlugin from '../../assets/images/calander_plugin.svg';
import heartDetails from '../../assets/images/heart_details.svg';
import chatIcon from '../../assets/images/chat_icon.svg';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;
const getCurrentPageURL = () => window.location.href;

interface LocalizedField {
  en: string;
  ar: string;
}

interface Comment {
  _id?: string;
  user: string;
  text: string;
  approved?: boolean;
}

interface BlogPost {
  _id: string;
  serviceTitle: LocalizedField;
  serviceTopic: LocalizedField;
  description: LocalizedField;
  tag: LocalizedField;
  priority?: number;
  isActive: boolean;
  image?: string;
  createdAt: string;
  likes: number;
  comments: Comment[];
}

const titleToSlug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const generateCaptcha = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 6 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
};

const BlogDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvedComments, setApprovedComments] = useState<Comment[]>([]);
  const [captchaCode, setCaptchaCode] = useState(generateCaptcha());

  const [formData, setFormData] = useState({
    name: '',
    message: '',
    captcha: '',
  });

  const [errors, setErrors] = useState<{
    name?: string;
    message?: string;
    captcha?: string;
  }>({});

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await fetchBlogs();
        const activeBlogs: BlogPost[] = (data.blogs as BlogPost[])
          .filter((b: BlogPost) => b.isActive)
          .sort(
            (a: BlogPost, b: BlogPost) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        setBlogs(activeBlogs);
      } catch {
        setError('failed_to_load_blogs');
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [currentLang, t]);

  const decodedSlug = slug ? decodeURIComponent(slug) : '';
  const post = blogs.find(
    (p) => titleToSlug(p.serviceTitle.en) === decodedSlug
  );

  const recentPosts = blogs
    .filter((p) => titleToSlug(p.serviceTitle.en) !== decodedSlug)
    .slice(0, 5);

  const postTags = post?.tag[currentLang].split(',').map((t) => t.trim()) || [];

  const handleRecentPostClick = (title: string) => {
    navigate(`/blog/${titleToSlug(title)}`);
  };

  const refreshCaptcha = () => {
    setCaptchaCode(generateCaptcha());
    setFormData({ ...formData, captcha: '' });
    setErrors({ ...errors, captcha: '' });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    const key = id.replace('Input', '').toLowerCase();
    setFormData({ ...formData, [key]: value });
    setErrors({ ...errors, [key]: '' });
  };

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.captcha.trim().toUpperCase() !== captchaCode.toUpperCase()) {
      newErrors.captcha = 'Incorrect CAPTCHA. Try again.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/blogs/${post?._id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: formData.name,
          text: formData.message,
        }),
      });

      if (!res.ok) throw new Error('Failed to submit comment');

      alert('comment submitted');
      setFormData({ name: '', message: '', captcha: '' });
      refreshCaptcha();
      loadApprovedComments();
    } catch (err) {
      alert('comment submit error');
      console.error(err);
    }
  };

  const handleLike = async () => {
    const likedKey = `liked_${post?._id}`;
    if (localStorage.getItem(likedKey)) {
      alert('already liked');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/blogs/${post?._id}/like`, {
        method: 'POST',
      });

      if (!res.ok) throw new Error('Failed to like');

      localStorage.setItem(likedKey, 'true');
      setBlogs((prev) =>
        prev.map((b) =>
          b._id === post?._id ? { ...b, likes: (b.likes || 0) + 1 } : b
        )
      );
    } catch (err) {
      console.error(err);
      alert('like_error');
    }
  };

  const loadApprovedComments = async () => {
    if (!post?._id) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/blogs/${post._id}/comments/approved?lang=${currentLang}`
      );
      const data = await res.json();
      setApprovedComments(data.comments || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (post) loadApprovedComments();
  }, [post, currentLang]);

  const currentPageURL = encodeURIComponent(getCurrentPageURL());
  const shareTitle = encodeURIComponent(
    post?.serviceTitle[currentLang] || 'Check out this blog post!'
  );
  const facebookShareURL = `https://www.facebook.com/sharer/sharer.php?u=${currentPageURL}`;
  const twitterShareURL = `https://twitter.com/intent/tweet?url=${currentPageURL}&text=${shareTitle}`;
  const linkedinShareURL = `https://www.linkedin.com/shareArticle?mini=true&url=${currentPageURL}&title=${shareTitle}`;
  const instagramShareURL = `https://www.instagram.com/`;
  const whatsappShareURL = `https://wa.me/?text=${shareTitle}%20${currentPageURL}`;

  if (loading) return <div className="text-center py-5"></div>;
  if (!post) return <div className="text-center py-5"></div>;

  return (
    <>
      <section className="blogdetail_heros bg-light">
        <div className="container text-center">
          <div className="main-title mb-3">
            <h2>{t('Resources_of_Shah_LPO')}</h2>
            <p className="text-muted">{t('Trust_Expertise_Results')}</p>
          </div>
        </div>
      </section>

      <section className="blogg-details-page py-5">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-6 mb-4">
              <img
                src={`${IMAGE_BASE_URL}/${post.image}`}
                className="img-fluid rounded shadow-sm"
                alt={post.serviceTitle[currentLang]}
              />
            </div>
            <div
              className="col-md-6"
              dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
            >
              <div className="main-title">
                <h2>{post.serviceTitle[currentLang]}</h2>
                <h3>{post.serviceTopic[currentLang]}</h3>
              </div>
              <div className="details_info">
                <p>{post.description[currentLang]}</p>
              </div>
              <div className="social_info d-flex gap-4 align-items-center mb-4">
                <div className="social_info_wrapes d-flex align-items-center gap-2">
                  <img
                    src={calendarPlugin}
                    alt="Calendar"
                    style={{ width: '20px' }}
                  />
                  <span>
                    {new Date(post.createdAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div
                  className="social_info_wrapes d-flex align-items-center gap-2"
                  onClick={handleLike}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={heartDetails} alt="Like" />
                  <span>{post.likes || 0}</span>
                </div>
                <div className="social_info_wrapes d-flex align-items-center gap-2">
                  <img src={chatIcon} alt="Comment" />
                  <span>{post.comments.length}</span>
                </div>
              </div>

              <div className="approved-comments mt-5">
                <p className="mb-2">Comments</p>
                {approvedComments.length === 0 ? (
                  <p className="text-muted">No comments yet</p>
                ) : (
                  <div>
                    {approvedComments.map((c, i) => (
                      <div
                        key={i}
                        className="border comment-list rounded p-3 mb-3 bg-light"
                      >
                        <strong style={{ fontSize: '14px' }}>{c.user}</strong>
                        <p className="mb-0" style={{ fontSize: '13px' }}>
                          {c.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-7">
              <div className="main-title mb-4">
                <h4 className="fw-bold">Leave a Comment</h4>
              </div>

              {/* Name Input */}
              <div className="mb-3">
                <label htmlFor="nameInput" className="form-label fw-semibold">
                  Name
                </label>
                <input
                  type="text"
                  id="nameInput"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Your name"
                  style={{ outline: 'none', boxShadow: 'none' }}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name}</small>
                )}
              </div>

              {/* Message Input */}
              <div className="mb-3">
                <label
                  htmlFor="messageInput"
                  className="form-label fw-semibold"
                >
                  Message
                </label>
                <textarea
                  id="messageInput"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Write your comment here..."
                  style={{ outline: 'none', boxShadow: 'none' }}
                />
                {errors.message && (
                  <small className="text-danger">{errors.message}</small>
                )}
              </div>

              {/* CAPTCHA Section */}
              <div className="mb-4">
                <label className="form-label fw-semibold mb-2">CAPTCHA</label>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div
                    className="captcha-code-box border px-3 py-2 bg-dark text-white rounded fw-bold"
                    style={{ fontSize: '12px' }}
                  >
                    {captchaCode}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={refreshCaptcha}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                  </button>
                  <input
                    type="text"
                    id="captchaInput"
                    value={formData.captcha}
                    onChange={handleChange}
                    className="form-control form-control-sm w-auto"
                    placeholder="Enter CAPTCHA"
                    style={{
                      minWidth: '140px',
                      outline: 'none',
                      boxShadow: 'none',
                    }}
                  />
                  <button
                    onClick={handleSubmit}
                    className="btn btn-sm btn-primary px-3"
                  >
                    Submit
                  </button>
                </div>
                {errors.captcha && (
                  <small className="text-danger">{errors.captcha}</small>
                )}
              </div>

              {/* Social Icons */}
              <div className="mb-4">
                <label className="form-label fw-semibold mb-2">
                  Social Share
                </label>
                <div className="icons mt-1" style={{ marginLeft: '-10px' }}>
                  <a
                    href={facebookShareURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-wrapper facebook-bg">
                      <svg
                        className="icon-svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.5"
                      >
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                      </svg>
                    </div>
                  </a>

                  <a
                    href={twitterShareURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-wrapper twitter-bg">
                      <svg
                        className="icon-svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.5"
                      >
                        <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                      </svg>
                    </div>
                  </a>

                  <a
                    href={linkedinShareURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-wrapper linkedin-bg">
                      <svg
                        className="icon-svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.5"
                      >
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                        <rect x="2" y="9" width="4" height="12" />
                        <circle cx="4" cy="4" r="2" />
                      </svg>
                    </div>
                  </a>

                  <a
                    href={instagramShareURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-wrapper instagram-bg">
                      <svg
                        className="icon-svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.5"
                      >
                        <rect
                          x="2"
                          y="2"
                          width="20"
                          height="20"
                          rx="5"
                          ry="5"
                        />
                        <circle cx="12" cy="12" r="4" />
                        <circle cx="18" cy="6" r="1" />
                      </svg>
                    </div>
                  </a>

                  <a
                    href={whatsappShareURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="icon-wrapper whatsapp-bg">
                      <svg
                        className="icon-svg"
                        viewBox="0 0 455.731 455.731"
                        fill="#FFFFFF"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path
                          d="M68.494,387.41l22.323-79.284c-14.355-24.387-21.913-52.134-21.913-80.638
                          c0-87.765,71.402-159.167,159.167-159.167s159.166,71.402,159.166,159.167c0,87.765-71.401,159.167-159.166,159.167
                          c-27.347,0-54.125-7-77.814-20.292L68.494,387.41z M154.437,337.406l4.872,2.975c20.654,12.609,44.432,19.274,68.762,19.274
                          c72.877,0,132.166-59.29,132.166-132.167S300.948,95.321,228.071,95.321S95.904,154.611,95.904,227.488
                          c0,25.393,7.217,50.052,20.869,71.311l3.281,5.109l-12.855,45.658L154.437,337.406z"
                        />
                        <path
                          d="M183.359,153.407l-10.328-0.563c-3.244-0.177-6.426,0.907-8.878,3.037
                          c-5.007,4.348-13.013,12.754-15.472,23.708c-3.667,16.333,2,36.333,16.667,56.333c14.667,20,42,52,90.333,65.667
                          c15.575,4.404,27.827,1.435,37.28-4.612c7.487-4.789,12.648-12.476,14.508-21.166l1.649-7.702c0.524-2.448-0.719-4.932-2.993-5.98
                          l-34.905-16.089c-2.266-1.044-4.953-0.384-6.477,1.591l-13.703,17.764c-1.035,1.342-2.807,1.874-4.407,1.312
                          c-9.384-3.298-40.818-16.463-58.066-49.687c-0.748-1.441-0.562-3.19,0.499-4.419l13.096-15.15
                          c1.338-1.547,1.676-3.722,0.872-5.602l-15.046-35.201C187.187,154.774,185.392,153.518,183.359,153.407z"
                        />
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-5">
              <div className="right_side">
                <div className="recent_post mb-4">
                  <div className="main-title">
                    <h3>Recent Post</h3>
                  </div>
                  {recentPosts.map((p, idx) => (
                    <div
                      key={idx}
                      className="total_post"
                      onClick={() => handleRecentPostClick(p.serviceTitle.en)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="date d-flex align-items-center gap-2">
                        <span>
                          <i className="bi bi-calendar3 me-2"></i>
                          {new Date(post.createdAt).toLocaleDateString(
                            'en-GB',
                            {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            }
                          )}
                        </span>
                      </div>
                      <h6 className="mb-0">{p.serviceTitle[currentLang]}</h6>
                    </div>
                  ))}
                  <div className="all_tags py-3">
                    <div className="main-title ">
                      <h3 className="">Tags</h3>
                    </div>
                    <div className="tag_list">
                      {postTags.map((tag) => (
                        <div className="tag_wrapper" key={tag}>
                          <a>{tag}</a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;
